import { pb } from "./pocketbase";

/**
 * taxonomySeedService.js — Seed e Gestão da Taxonomia Canônica (P2.1)
 *
 * Popula:
 * - `produto_categorias`: Categorias temáticas (provenientes dos Boards do Monday e do catálogo).
 * - `produto_tipos`: Tipos de joias vinculados aos prefixos de SKU (RIN, PIN, BER, DIR, MED, COR, PUL, ALI, EXC, ESC).
 */

export const INITIAL_CATEGORIES = [
  "ENGENHARIAS",
  "SAÚDE E BEM ESTAR",
  "HUMANAS E SOCIAIS",
  "EXATAS E TECNOLOGIA",
  "JOIAS RELIGIOSAS",
  "PETS & ANIMAIS",
  "PROFISSÕES DIVERSAS",
  "MILITARES & SEGURANÇA",
  "CURSOS DIVERSOS",
  "COLEÇÃO EXCLUSIVA",
  "OUTROS"
];

export const INITIAL_TIPOS = [
  { nome: "Anel", prefixo_sku: "RIN" },
  { nome: "Pingente", prefixo_sku: "PIN" },
  { nome: "Berloque", prefixo_sku: "BER" },
  { nome: "Anel de Formatura", prefixo_sku: "DIR" },
  { nome: "Medalha", prefixo_sku: "MED" },
  { nome: "Corrente", prefixo_sku: "COR" },
  { nome: "Pulseira", prefixo_sku: "PUL" },
  { nome: "Aliança", prefixo_sku: "ALI" },
  { nome: "Joia Exclusiva", prefixo_sku: "EXC" },
  { nome: "Escapulário", prefixo_sku: "ESC" }
];

export const taxonomySeedService = {
  /**
   * Executa a semeadura idempotente de categorias e tipos de joias.
   * Não duplica se o registro já existir.
   */
  async seedTaxonomy() {
    const results = {
      categoriasCriadas: 0,
      categoriasExistentes: 0,
      tiposCriados: 0,
      tiposExistentes: 0,
      erros: []
    };

    console.log("🌱 taxonomySeedService: Iniciando seed de taxonomia...");

    // 1. SEED DE CATEGORIAS
    try {
      const existingCategoriesRecords = await pb.collection("produto_categorias").getFullList();
      const existingCategoryNames = new Set(
        existingCategoriesRecords.map(c => c.nome?.trim().toUpperCase())
      );

      for (const catName of INITIAL_CATEGORIES) {
        const cleanName = catName.trim().toUpperCase();
        if (existingCategoryNames.has(cleanName)) {
          results.categoriasExistentes++;
        } else {
          try {
            await pb.collection("produto_categorias").create({
              nome: catName,
              ativo: true
            });
            results.categoriasCriadas++;
            console.log(`✅ Categoria criada: ${catName}`);
          } catch (err) {
            console.error(`❌ Erro ao criar categoria ${catName}:`, err);
            results.erros.push(`Categoria ${catName}: ${err.message}`);
          }
        }
      }
    } catch (err) {
      console.error("❌ Erro ao listar produto_categorias:", err);
      results.erros.push(`Falha ao acessar produto_categorias: ${err.message}`);
    }

    // 2. SEED DE TIPOS DE JOIA
    try {
      const existingTiposRecords = await pb.collection("produto_tipos").getFullList();
      const existingPrefixes = new Set(
        existingTiposRecords.map(t => t.prefixo_sku?.trim().toUpperCase())
      );

      for (const tipo of INITIAL_TIPOS) {
        const cleanPrefix = tipo.prefixo_sku.trim().toUpperCase();
        if (existingPrefixes.has(cleanPrefix)) {
          results.tiposExistentes++;
        } else {
          try {
            await pb.collection("produto_tipos").create({
              nome: tipo.nome,
              prefixo_sku: tipo.prefixo_sku.toUpperCase(),
              ativo: true
            });
            results.tiposCriados++;
            console.log(`✅ Tipo criado: ${tipo.nome} (${tipo.prefixo_sku})`);
          } catch (err) {
            console.error(`❌ Erro ao criar tipo ${tipo.nome}:`, err);
            results.erros.push(`Tipo ${tipo.nome}: ${err.message}`);
          }
        }
      }
    } catch (err) {
      console.error("❌ Erro ao listar produto_tipos:", err);
      results.erros.push(`Falha ao acessar produto_tipos: ${err.message}`);
    }

    console.log("🌱 taxonomySeedService finalizado:", results);
    return results;
  },

  /**
   * Busca todas as categorias ativas
   */
  async getCategorias() {
    try {
      return await pb.collection("produto_categorias").getFullList({
        filter: "ativo = true",
        sort: "+nome"
      });
    } catch (err) {
      console.error("Erro ao buscar produto_categorias:", err);
      return [];
    }
  },

  /**
   * Busca todos os tipos ativas
   */
  async getTipos() {
    try {
      return await pb.collection("produto_tipos").getFullList({
        filter: "ativo = true",
        sort: "+nome"
      });
    } catch (err) {
      console.error("Erro ao buscar produto_tipos:", err);
      return [];
    }
  }
};
